import creditCardType from 'credit-card-type';
import { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';

import API from 'my-core/api';
import { endOfSemester, formatDate, monthAndYearToDate } from 'my-core/date-utils';

import OnlineCoursePurchaseFormCourseItem from './OnlineCoursePurchaseFormCourseItem';

import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { faLockAlt, faQuestionCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import amexDark from 'images/third-party/credit-cards/amex-dark.png';
import amexLight from 'images/third-party/credit-cards/amex-light.png';
import masterCardDark from 'images/third-party/credit-cards/master-card-dark.png';
import masterCardLight from 'images/third-party/credit-cards/master-card-light.png';
import visaDark from 'images/third-party/credit-cards/visa-dark.png';
import visaLight from 'images/third-party/credit-cards/visa-light.png';
import paypalSrc from 'images/third-party/PayPal.svg';

const CARD_ICON_MAP = [
  ['visa', visaLight, visaDark],
  ['american-express', amexLight, amexDark],
  ['mastercard', masterCardLight, masterCardDark],
];

const contentCss = ({ breakpoints, palette, shape: { borderRadius }, spacing }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(3),

  '.OCPFP-paymentOptions': {
    border: `1px solid ${palette.divider}`,
    borderRadius,
    overflow: 'hidden',
  },
  '.OCPFP-paymentOption': {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: spacing(1, 2),
    backgroundColor: palette.background.paper,
    '&.OCPFP-paymentOption_disabled': {
      '& > *': { opacity: 0.5 },
    },
  },
  '.OCPFP-radio': {
    padding: 0,
    marginRight: spacing(1.5),
    '&:hover': { backgroundColor: 'transparent' },
  },
  '.OCPFP-spacer': { flex: 1 },
  '.OCPFP-card': {
    width: 36,
    marginLeft: spacing(0.5),
    borderRadius,
    border: `1px solid ${palette.grey[200]}`,
  },
  '.OCPFP-paypalImage': { height: 20 },

  '.OCPFP-cardFields': {
    padding: spacing(1, 2, 1.5),
  },

  '.OCPFP-cardFieldsInline': {
    display: 'flex',
    gap: spacing(1.5),
  },
  '.OCPFP-hidden': { display: 'none' },

  [breakpoints.only('xs')]: {
    '.OCPFP-cardFieldsInline': {
      flexDirection: 'column',
      '& > * + *': { marginLeft: 0 },
    },
  },
});

export default function OnlineCoursePurchaseFormPayment(props) {
  const { course, header, onlineCourse, onShowTerms, onSubmitPurchase, paymentDetails, paypalLoaded, purchasing } =
    props;

  const submitButton = useRef(null);
  const paypalId = useRef(uuidV4());

  const [fields, setFields] = useState({
    card_expiry_month: '',
    card_expiry_year: '',
    card_number: '',
    card_verification: '',
    cardholder_name: '',
  });
  const [paymentOption, setPaymentOption] = useState('card');

  useEffect(() => {
    if (!paypalLoaded) return;
    if (!window.paypal) return;
    window.paypal.Button.render(
      {
        env: process.env.MERCHANT_MODE === 'test' ? 'sandbox' : 'production',
        // https://developer.paypal.com/docs/archive/checkout/how-to/customize-button/#button-styles
        style: { color: 'gold', shape: 'pill', size: 'medium', tagline: false },
        commit: true,
        payment: () => {
          return API.postJSON(
            ...[
              '/online_course_users/express_checkout',
              { online_course_id: onlineCourse.id, expected_total: paymentDetails.total },
            ],
          ).then(response => response.params.token);
        },
        onAuthorize: data => {
          onSubmitPurchase({
            express_token: data.paymentToken,
            express_payer_id: data.payerID,
          });
        },
        onCancel: () => console.error('PayPal payment cancelled'),
        onError: err => console.error(err),
      },
      paypalId.current,
    );
  }, [onSubmitPurchase, onlineCourse.id, paymentDetails.total, paypalLoaded]);

  function handleFormSubmit(event) {
    event.preventDefault();
    if (purchasing) return;

    const { card_expiry_month, card_expiry_year, ...rest } = fields;
    onSubmitPurchase({
      ...rest,
      card_expires_on: monthAndYearToDate(card_expiry_month, card_expiry_year),
    });
  }

  function handleCardSubmit() {
    // triggers form validation
    submitButton.current.click();
  }

  const cards = useMemo(() => {
    const types = fields.card_number ? creditCardType(fields.card_number) : [];
    const type = types[0] && types[0].type;
    return CARD_ICON_MAP.map(t => <img key={t[0]} className="OCPFP-card" src={type === t[0] ? t[2] : t[1]} />);
  }, [fields.card_number]);

  return (
    <>
      <DialogContent>
        {header}
        <div css={contentCss}>
          <OnlineCoursePurchaseFormCourseItem
            course={course}
            onlineCourse={onlineCourse}
            total={paymentDetails.total}
          />
          <Divider />
          <Typography
            sx={{
              color: 'text.secondary',
            }}
            variant="body2"
          >
            All transactions are secure and encrypted.
          </Typography>
          <div className="OCPFP-paymentOptions">
            {renderPanel(
              'card',
              <>
                <Typography>Credit Card</Typography>
                <div className="OCPFP-spacer" />
                {cards}
              </>,
              renderCardFields(),
            )}
            <Divider />
            {renderPanel(
              'paypal',
              <>
                <img alt="paypal logo" className="OCPFP-paypalImage" src={paypalSrc} />
                <div className="OCPFP-spacer" />
              </>,
            )}
          </div>
          <div>
            <Typography
              align="left"
              component="p"
              sx={{
                color: 'text.secondary',
              }}
              variant="caption"
            >
              {`Course will accessible until ${formatDate(endOfSemester(), 'dateMedium')}.`}
            </Typography>
            <Typography
              align="left"
              component="p"
              sx={{
                color: 'text.secondary',
              }}
              variant="caption"
            >
              By completing your purchase, you agree to these{' '}
              <Link color="primary" component="button" onClick={onShowTerms} variant="caption">
                Terms of Service
              </Link>
              , including but not exclusive to, no refunds and/or sharing of accounts.
            </Typography>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        {paymentOption !== 'paypal' && (
          <Button disabled={purchasing} onClick={handleCardSubmit} variant="contained">
            Complete Payment
          </Button>
        )}
        <div id={paypalId.current} style={paymentOption === 'paypal' ? undefined : { display: 'none' }} />
      </DialogActions>
    </>
  );

  function renderPanel(value, title, content) {
    const active = paymentOption === value;

    return (
      <>
        <ButtonBase
          classes={{
            root: 'OCPFP-paymentOption',
            disabled: 'OCPFP-paymentOption_disabled',
          }}
          disabled={purchasing}
          disableRipple
          onClick={() => setPaymentOption(value)}
        >
          <Radio
            checked={active}
            className="OCPFP-radio"
            color="primary"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            name={`payment-option-${value}`}
            value={value}
          />
          {title}
        </ButtonBase>
        {!!content && (
          <Collapse in={!purchasing && active}>
            <Divider />
            {content}
          </Collapse>
        )}
      </>
    );
  }

  function renderCardFields() {
    const commonFieldProps = {
      disabled: purchasing,
      fullWidth: true,
      margin: 'dense',
      size: 'small',
      required: true,
    };
    return (
      <form className="OCPFP-cardFields" onSubmit={handleFormSubmit}>
        <TextField
          {...commonFieldProps}
          autoComplete="cc-name"
          label="Name on Card"
          name="ccname"
          onChange={({ target: { value } }) => setFields({ ...fields, cardholder_name: value })}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <FontAwesomeIcon icon={faLockAlt} />
                </InputAdornment>
              ),
            },
          }}
          value={fields.cardholder_name}
        />
        <TextField
          {...commonFieldProps}
          autoComplete="cc-number"
          label="Card Number"
          name="cardnumber"
          onChange={({ target: { value } }) => setFields({ ...fields, card_number: value })}
          slotProps={{
            htmlInput: {
              maxLength: 16,
              //https://developers.google.com/web/fundamentals/design-and-ux/input/forms/#use_metadata_to_enable_auto-complete
              pattern:
                '^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|6(?:011|5[0-9]{2})[0-9]{12}|(?:2131|1800|35d{3})d{11})$',
            },
          }}
          value={fields.card_number}
        />
        <div className="OCPFP-cardFieldsInline">
          <TextField
            {...commonFieldProps}
            autoComplete="cc-exp-month"
            label="Exp. Month"
            name="ccmonth"
            onChange={({ target: { value } }) =>
              setFields({
                ...fields,
                card_expiry_month: parseInt(value).toString().padStart(2, 0),
              })
            }
            placeholder="MM"
            slotProps={{
              htmlInput: {
                min: 1,
                max: 12,
                pattern: 'd*',
              },
            }}
            type="number"
            value={fields.card_expiry_month}
          />
          <TextField
            {...commonFieldProps}
            autoComplete="cc-exp-year"
            label="Exp. Year"
            name="ccyear"
            onChange={({ target: { value } }) => setFields({ ...fields, card_expiry_year: value })}
            placeholder="YYYY"
            slotProps={{
              htmlInput: {
                min: 2018,
                max: 2099,
                // for iOS '\d*'
                pattern: 'd*',
              },
            }}
            type="number"
            value={fields.card_expiry_year}
          />
          <TextField
            {...commonFieldProps}
            autoComplete="cc-csc"
            label="CVC"
            name="cvc"
            onChange={({ target: { value } }) => setFields({ ...fields, card_verification: value })}
            slotProps={{
              input: {
                endAdornment: (
                  <Tooltip placement="top-end" title="This is the security code found on the back of your credit card">
                    <InputAdornment position="end">
                      <FontAwesomeIcon icon={faQuestionCircle} />
                    </InputAdornment>
                  </Tooltip>
                ),
              },
            }}
            type="number"
            value={fields.card_verification}
          />
          {/* hidden but necessary */}
          <button
            className="OCPFP-hidden"
            disabled={purchasing || paymentOption !== 'card'}
            ref={submitButton}
            type="submit"
          >
            Submit
          </button>
        </div>
      </form>
    );
  }
}
